import React, { useEffect, useRef, useState } from 'react';
import ModalCloseButton from './ModalCloseButton';
import {
  collectAll,
  collectAllAsZip,
  downloadAsJson,
  downloadBlob,
  defaultExportFilename,
} from '../lib/dataExport';
import {
  parseImportText,
  summarisePayload,
  importIntoUser,
  ImportError,
} from '../lib/dataImport';

// DataExportImportModal — single popup invoked from the "import / export"
// row in AccountPopover. Lays out three options:
//   • export as JSON (full backup, re-importable)
//   • export as text .zip (readable files, lossy)
//   • import from JSON (creates NEW boards, never overwrites)
//
// All state lives inside the modal so AccountPopover stays compact.
export default function DataExportImportModal({ open, onClose, user }) {
  const [busy, setBusy] = useState(null); // 'json' | 'zip' | 'import' | null
  const [errorMsg, setErrorMsg] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [preview, setPreview] = useState(null); // { payload, counts } | null
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setBusy(null);
      setErrorMsg('');
      setStatusMsg('');
      setPreview(null);
    }
  }, [open]);

  if (!open || !user) return null;

  const handleExportJson = async () => {
    if (busy) return;
    setBusy('json');
    setErrorMsg('');
    setStatusMsg('');
    try {
      const data = await collectAll(user);
      downloadAsJson(data, defaultExportFilename(new Date(), 'json'));
      setStatusMsg('exported.');
    } catch (err) {
      console.warn('[export json] failed:', err);
      setErrorMsg('Could not export your data. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const handleExportZip = async () => {
    if (busy) return;
    setBusy('zip');
    setErrorMsg('');
    setStatusMsg('');
    try {
      const blob = await collectAllAsZip(user);
      downloadBlob(blob, defaultExportFilename(new Date(), 'zip'));
      setStatusMsg('exported.');
    } catch (err) {
      console.warn('[export zip] failed:', err);
      setErrorMsg('Could not export your data. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const handlePickFile = () => {
    if (busy) return;
    setErrorMsg('');
    setStatusMsg('');
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setBusy('import');
    setErrorMsg('');
    setStatusMsg('reading file…');
    try {
      const text = await file.text();
      const payload = parseImportText(text);
      const counts = summarisePayload(payload);
      setPreview({ payload, counts });
      setStatusMsg('');
    } catch (err) {
      if (err instanceof ImportError) {
        setErrorMsg(err.message);
      } else {
        console.warn('[import parse] failed:', err);
        setErrorMsg('Could not read the file.');
      }
      setStatusMsg('');
    } finally {
      setBusy(null);
    }
  };

  const handleConfirmImport = async () => {
    if (!preview || busy) return;
    setBusy('import');
    setErrorMsg('');
    setStatusMsg('importing…');
    try {
      const result = await importIntoUser(user, preview.payload);
      const parts = [
        `${result.boardsCreated} board${plural(result.boardsCreated)}`,
        `${result.listsCreated} list${plural(result.listsCreated)}`,
        `${result.remindersCreated} reminder${plural(result.remindersCreated)}`,
      ];
      let msg = `imported ${parts.join(', ')}.`;
      if (result.remindersSkipped > 0) {
        msg += ` skipped ${result.remindersSkipped} orphan reminder${plural(result.remindersSkipped)}.`;
      }
      setStatusMsg(msg);
      setPreview(null);
    } catch (err) {
      if (err instanceof ImportError) {
        setErrorMsg(err.message);
      } else {
        console.warn('[import] failed:', err);
        setErrorMsg('Could not import the file. Please try again.');
      }
      setStatusMsg('');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 3500,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.15s ease',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          maxWidth: '460px',
          width: '100%',
          padding: '22px 24px',
          boxShadow: 'var(--shadow-modal-heavy)',
          fontFamily: 'var(--mono)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
            import / export your data
          </h2>
          <ModalCloseButton onClick={onClose} style={{ position: 'static' }} />
        </div>

        <p style={{
          fontSize: '11px', color: 'var(--text-dim)', lineHeight: 1.55,
          margin: '0 0 14px',
        }}>
          download your boards, lists, and reminders, or import a JSON
          export from another listicity account. importing always creates
          new boards — it never overwrites or merges into existing ones.
        </p>

        {!preview && (
          <>
            <OptionRow
              icon={<JsonIcon />}
              title="export as JSON"
              subtitle="full backup · re-importable"
              onClick={handleExportJson}
              busy={busy === 'json'}
            />
            <OptionRow
              icon={<ZipIcon />}
              title="export as text .zip"
              subtitle="readable text files · positions and dates dropped"
              onClick={handleExportZip}
              busy={busy === 'zip'}
            />
            <OptionRow
              icon={<ImportIcon />}
              title="import from JSON"
              subtitle="creates new boards · never overwrites"
              onClick={handlePickFile}
              busy={busy === 'import'}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
          </>
        )}

        {preview && (
          <div style={{
            padding: '12px 14px',
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            marginBottom: 12,
            fontSize: '12px',
            color: 'var(--text)',
            lineHeight: 1.6,
          }}>
            <div style={{ marginBottom: 6 }}>this file contains:</div>
            <ul style={{ margin: '0 0 8px', paddingLeft: 18, color: 'var(--text-dim)' }}>
              <li>{preview.counts.boards} board{plural(preview.counts.boards)}</li>
              <li>{preview.counts.lists} list{plural(preview.counts.lists)}</li>
              <li>{preview.counts.reminders} reminder{plural(preview.counts.reminders)}</li>
            </ul>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <ModalButton onClick={() => { setPreview(null); setStatusMsg(''); }} disabled={busy === 'import'}>
                cancel
              </ModalButton>
              <ModalButton accent onClick={handleConfirmImport} disabled={busy === 'import'}>
                {busy === 'import' ? 'importing…' : 'import'}
              </ModalButton>
            </div>
          </div>
        )}

        {statusMsg && (
          <div style={{
            marginTop: 8,
            fontSize: '11px',
            color: 'var(--accent, var(--text))',
            lineHeight: 1.5,
          }}>
            {statusMsg}
          </div>
        )}
        {errorMsg && (
          <div style={{
            marginTop: 8,
            fontSize: '11px',
            color: 'var(--danger, #d04a4a)',
            lineHeight: 1.5,
          }}>
            {errorMsg}
          </div>
        )}
      </div>
    </div>
  );
}

function OptionRow({ icon, title, subtitle, onClick, busy }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={busy ? undefined : onClick}
      onMouseEnter={() => !busy && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 14px',
        marginBottom: 8,
        background: hover && !busy ? 'var(--surface2)' : 'var(--bg, transparent)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        cursor: busy ? 'default' : 'pointer',
        opacity: busy ? 0.6 : 1,
        transition: 'background 0.1s',
      }}
    >
      <span style={{ color: 'var(--text-dim)', flexShrink: 0 }}>{icon}</span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: '12px', color: 'var(--text)' }}>
          {busy ? 'working…' : title}
        </span>
        <span style={{
          fontSize: '10px', color: 'var(--text-dimmer)', marginTop: 2,
        }}>
          {subtitle}
        </span>
      </span>
    </div>
  );
}

function ModalButton({ children, onClick, accent, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 14px',
        background: accent ? 'var(--accent)' : 'transparent',
        color: accent ? 'var(--bg)' : 'var(--text)',
        border: accent ? '1px solid var(--accent)' : '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: '11px',
        fontWeight: accent ? 600 : 400,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {children}
    </button>
  );
}

function plural(n) { return n === 1 ? '' : 's'; }

function JsonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 1v8M4 6l3 3 3-3M2 11.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ZipIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="10" height="10" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4v1M7 6v1M7 8v1M7 10v1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 13V5M4 8l3-3 3 3M2 2.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
